import { RootState } from ".";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import * as authAPI from "../api/auth";
import { User } from "../types/user";

export interface AuthState {
  returnLocation: string | null;
  signupWithoutWorkspace: boolean;
  user: User | null;
}

export const checkUser = createAsyncThunk("auth/checkUser", async () => {
  return await authAPI.getMe();
});

export const signIn = createAsyncThunk(
  "auth/signIn",
  async ({ username, password }: { username: string; password: string }) => {
    try {
      return await authAPI.signIn(username, password);
    } catch (e) {
      const err = new Error(e.response.data.error);
      (err as any).code = e.response.data.code;
      throw err;
    }
  },
);

export const signUp = createAsyncThunk<
  User,
  { username: string; password: string },
  { state: RootState }
>("auth/signUp", async ({ username, password }, { getState }) => {
  try {
    const {
      auth: { signupWithoutWorkspace },
    } = getState();
    return await authAPI.signUp(username, password, signupWithoutWorkspace);
  } catch (e) {
    throw new Error(e.response.data.error);
  }
});

export const authSlice = createSlice({
  name: "user",
  initialState: {
    returnLocation: null,
    user: null,
  } as AuthState,
  reducers: {
    setReturnLocation: (state, action: PayloadAction<string>) => {
      state.returnLocation = action.payload;
    },
    setSignupWithoutWorkspace: (state, action: PayloadAction<boolean>) => {
      state.signupWithoutWorkspace = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkUser.fulfilled, (state, action) => {
      state.user = action.payload;
    });
    builder.addCase(signIn.fulfilled, (state, action) => {
      state.user = action.payload;
    });
    builder.addCase(signUp.fulfilled, (state, action) => {
      state.user = action.payload;
    });
  },
});

export const { setReturnLocation, setSignupWithoutWorkspace, setUser } =
  authSlice.actions;

export default authSlice.reducer;
